import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha, Box, Collapse, Stack, Typography } from '@mui/material';
import type { HelpCategory, HelpPageListItem } from '/@/hooks/help-center';
import { _ } from '/@/locale';
import { sanitize } from '/@/utils';
import { HelpCategoryIcon } from './help-category-icon';

export type HelpCenterCategoryGroup = {
    category: HelpCategory;
    pages: HelpPageListItem[];
};

export const HELP_CATEGORY_ANCHOR_PREFIX = 'help-category';

export function helpCategoryAnchorId(categoryId: number, prefix = HELP_CATEGORY_ANCHOR_PREFIX): string {
    return `${prefix}-${categoryId}`;
}

type HelpCategoryGroupsProps = {
    groups: HelpCenterCategoryGroup[];
    isCategoryExpanded: (categoryId: number) => boolean;
    onToggleCategory: (categoryId: number) => void;
    onSelectPage: (slug: string) => void;
    anchorPrefix?: string;
};

export function HelpCategoryGroups({
    groups,
    isCategoryExpanded,
    onToggleCategory,
    onSelectPage,
    anchorPrefix = HELP_CATEGORY_ANCHOR_PREFIX,
}: HelpCategoryGroupsProps) {
    return (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
            {groups.map(({ category, pages: catPages }) => (
                <Box
                    key={category.id}
                    id={helpCategoryAnchorId(category.id, anchorPrefix)}
                    sx={{
                        width: '100%',
                        border: (t) => `1px solid ${t.palette.divider}`,
                        boxSizing: 'border-box',
                        scrollMarginTop: { xs: 88, md: 100 },
                        '&:hover': { borderColor: (t) => t.palette.gray[800] },
                    }}
                >
                    <Stack
                        component='button'
                        type='button'
                        onClick={() => onToggleCategory(category.id)}
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                        sx={{
                            width: '100%',
                            bgcolor: (t) => t.palette.primary.infoBackground,
                            px: 2,
                            py: 1.5,
                            cursor: 'pointer',
                            textAlign: 'left',
                            font: 'inherit',
                            color: 'inherit',
                            border: 0,
                            boxSizing: 'border-box',
                        }}
                    >
                        <Stack direction='row' alignItems='center' spacing={2} sx={{ minWidth: 0 }}>
                            <HelpCategoryIcon iconUrl={category.iconUrl} />
                            <Stack spacing={0.5} sx={{ minWidth: 0, textAlign: 'left' }}>
                                <Typography variant='h6' color='primary'>
                                    {_(category.title)}
                                </Typography>
                                {Boolean(sanitize(_(category.subtitle))) && (
                                    <Typography variant='body2' color='primary.mui.inactive'>
                                        {_(category.subtitle)}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                        {isCategoryExpanded(category.id) ? (
                            <ExpandLessIcon color='primary' sx={{ flexShrink: 0 }} />
                        ) : (
                            <ExpandMoreIcon color='primary' sx={{ flexShrink: 0 }} />
                        )}
                    </Stack>
                    <Collapse in={isCategoryExpanded(category.id)} timeout='auto' unmountOnExit>
                        <Stack sx={{ width: '100%' }}>
                            {catPages.map((p, idx) => (
                                <Stack
                                    key={p.id}
                                    component='button'
                                    type='button'
                                    direction='row'
                                    alignItems='center'
                                    spacing={2}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelectPage(p.slug);
                                    }}
                                    p={2}
                                    sx={{
                                        width: '100%',
                                        bgcolor: 'background.paper',
                                        border: 0,
                                        borderTop: (t) => `1px solid ${t.palette.divider}`,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        font: 'inherit',
                                        color: 'inherit',
                                        boxSizing: 'border-box',
                                        '&:hover': { bgcolor: (t) => alpha(t.palette.divider, 0.005) },
                                    }}
                                >
                                    <Stack
                                        direction='row'
                                        alignItems='center'
                                        spacing={1}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    >
                                        <Typography variant='subtitle1' color='primary' sx={{ textAlign: 'left' }}>
                                            {_(p.title)}
                                        </Typography>
                                    </Stack>
                                    <ChevronRightIcon color='primary' sx={{ flexShrink: 0 }} />
                                </Stack>
                            ))}
                        </Stack>
                    </Collapse>
                </Box>
            ))}
        </Stack>
    );
}
